import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Monitor, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function DeviceRegistration() {
  const navigate = useNavigate();
  const { householdId } = useParams();
  const [step, setStep] = useState(1); // 1: Register, 2: Pair, 3: Success
  const [serialNumber, setSerialNumber] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [enteredPairingCode, setEnteredPairingCode] = useState('');
  const [loading, setLoading] = useState(false);

  const registerDevice = async (e) => {
    e.preventDefault();
    if (!serialNumber.trim()) {
      toast.error('Bitte geben Sie eine Seriennummer ein');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/devices/register`, {
        serial_number: serialNumber
      });
      
      setDeviceId(response.data.id);
      setPairingCode(response.data.pairing_code);
      setStep(2);
      toast.success('Gerät erfolgreich registriert!');
    } catch (error) {
      console.error('Error registering device:', error);
      toast.error('Fehler beim Registrieren des Geräts');
    } finally {
      setLoading(false);
    }
  };

  const pairDevice = async (e) => {
    e.preventDefault();
    if (!enteredPairingCode.trim()) {
      toast.error('Bitte geben Sie den Pairing-Code ein');
      return;
    }

    if (!householdId) {
      toast.error('Haushalt-ID fehlt');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/devices/pair`, {
        pairing_code: enteredPairingCode,
        household_id: householdId
      });
      
      setStep(3);
      toast.success('Gerät erfolgreich mit Haushalt verbunden!');
    } catch (error) {
      console.error('Error pairing device:', error);
      toast.error('Fehler beim Verbinden des Geräts. Prüfen Sie den Pairing-Code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Gerät registrieren</h1>
          <p className="text-gray-600 mt-2">Registrieren und verbinden Sie ein neues Gerät</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center ${step >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              {step > 1 ? <Check className="w-6 h-6" /> : '1'}
            </div>
            <span className="ml-2 font-medium">Registrieren</span>
          </div>
          <div className={`w-24 h-1 mx-4 ${step >= 2 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${step >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              {step > 2 ? <Check className="w-6 h-6" /> : '2'}
            </div>
            <span className="ml-2 font-medium">Verbinden</span>
          </div>
          <div className={`w-24 h-1 mx-4 ${step >= 3 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${step >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              {step > 3 ? <Check className="w-6 h-6" /> : '3'}
            </div>
            <span className="ml-2 font-medium">Fertig</span>
          </div>
        </div>

        {/* Step 1: Register Device */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-6 h-6" />
                Schritt 1: Gerät registrieren
              </CardTitle>
              <CardDescription>
                Geben Sie die Seriennummer des Geräts ein, die sich auf der Rückseite befindet
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={registerDevice} className="space-y-4">
                <div>
                  <Label htmlFor="serialNumber">Seriennummer</Label>
                  <Input
                    id="serialNumber"
                    placeholder="z.B. HAYAT-DEV-001"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value)}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Format: HAYAT-XXX-XXX
                  </p>
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Registrierung läuft...' : 'Gerät registrieren'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Pair Device */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-6 h-6" />
                Schritt 2: Gerät verbinden
              </CardTitle>
              <CardDescription>
                Geben Sie den Pairing-Code ein, der auf dem Gerät angezeigt wird
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800 mb-2">✅ Gerät erfolgreich registriert!</p>
                <p className="text-sm text-gray-600">
                  <strong>Seriennummer:</strong> {serialNumber}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Ihr Pairing-Code:</strong> 
                  <span className="text-2xl font-mono font-bold ml-2 text-indigo-600">{pairingCode}</span>
                </p>
              </div>
              
              <form onSubmit={pairDevice} className="space-y-4">
                <div>
                  <Label htmlFor="pairingCode">Pairing-Code vom Gerät</Label>
                  <Input
                    id="pairingCode"
                    placeholder="6-stelliger Code"
                    value={enteredPairingCode}
                    onChange={(e) => setEnteredPairingCode(e.target.value)}
                    maxLength={6}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Der Code wird auf dem Gerät-Bildschirm angezeigt
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Zurück
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1">
                    {loading ? 'Verbindung läuft...' : 'Gerät verbinden'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <Check className="w-6 h-6" />
                Erfolgreich!
              </CardTitle>
              <CardDescription>
                Das Gerät wurde erfolgreich registriert und mit dem Haushalt verbunden
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Gerät ist einsatzbereit!</h3>
                <p className="text-gray-600 mb-4">
                  Seriennummer: <strong>{serialNumber}</strong>
                </p>
                <p className="text-sm text-gray-500">
                  Sie können das Gerät jetzt einrichten und einem Senior zuweisen.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setStep(1);
                    setSerialNumber('');
                    setDeviceId('');
                    setPairingCode('');
                    setEnteredPairingCode('');
                  }}
                  className="flex-1"
                >
                  Weiteres Gerät registrieren
                </Button>
                <Button 
                  onClick={() => navigate(`/households/${householdId}`)}
                  className="flex-1"
                >
                  Zurück zum Haushalt
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
